import { useEffect, useRef, useState } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  type EdgeProps,
  type EdgeTypes,
} from '@xyflow/react';
import { useFlowStore } from '../../store/flowStore';
import { resolveEdgeStroke, type AppEdge } from '../../types';

function LabeledEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  data,
  markerEnd,
  style,
}: EdgeProps<AppEdge>) {
  const updateEdge = useFlowStore((s) => s.updateEdge);
  const setEdges = useFlowStore((s) => s.setEdges);
  const takeSnapshot = useFlowStore((s) => s.takeSnapshot);
  const colorMode = useFlowStore((s) => s.colorMode);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(data?.label ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const pathType = data?.pathType ?? 'smoothstep';
  const params = {
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  };

  let path: string;
  let labelX: number;
  let labelY: number;
  switch (pathType) {
    case 'straight':
      [path, labelX, labelY] = getStraightPath(params);
      break;
    case 'step':
      [path, labelX, labelY] = getSmoothStepPath({ ...params, borderRadius: 0 });
      break;
    case 'smoothstep':
      [path, labelX, labelY] = getSmoothStepPath(params);
      break;
    default:
      [path, labelX, labelY] = getBezierPath(params);
  }

  const commit = () => {
    setEditing(false);
    if (draft !== (data?.label ?? '')) {
      updateEdge(id, { data: { label: draft } });
    }
  };

  const removeEdge = () => {
    if (useFlowStore.getState().readOnly || useFlowStore.getState().presentationMode) {
      return
    }
    takeSnapshot();
    setEdges(useFlowStore.getState().edges.filter((e) => e.id !== id));
  };

  const hasLabel = Boolean(data?.label);
  const showBadge = hasLabel || selected || editing;

  const storedStroke =
    typeof style?.stroke === 'string' && style.stroke ? style.stroke : undefined;
  const stroke = resolveEdgeStroke(storedStroke, colorMode);
  const edgeStyle = {
    strokeWidth: 2,
    ...style,
    stroke,
  };

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={edgeStyle}
      />
      {showBadge && (
        <EdgeLabelRenderer>
          <div
            className={`edge-label nodrag nopan ${selected ? 'selected' : ''}`}
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            }}
            onClick={() => {
              if (
                useFlowStore.getState().presentationMode ||
                useFlowStore.getState().readOnly
              ) {
                return
              }
              if (!selected) return
              setDraft(data?.label ?? '');
              setEditing(true);
            }}
            onDoubleClick={() => {
              if (
                useFlowStore.getState().presentationMode ||
                useFlowStore.getState().readOnly
              ) {
                return
              }
              setDraft(data?.label ?? '');
              setEditing(true);
            }}
          >
            {editing ? (
              <input
                ref={inputRef}
                className="edge-label-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commit();
                  if (e.key === 'Escape') setEditing(false);
                }}
              />
            ) : (
              <span className="edge-label-text">
                {data?.label || 'rótulo…'}
              </span>
            )}
            {selected && !editing && (
              <button
                type="button"
                className="edge-delete-btn"
                title="Remover conexão"
                onClick={removeEdge}
                aria-label="Remover conexão"
              >
                <svg viewBox="0 0 10 10" aria-hidden="true">
                  <path
                    d="M2 2l6 6M8 2L2 8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export const edgeTypes: EdgeTypes = {
  labeled: LabeledEdge,
};
